import { jsx as _jsx } from "react/jsx-runtime";
/**
 * React Auth Provider
 *
 * Provides authentication context to the React component tree.
 */
import { createContext, useContext, useEffect, useState, useCallback, } from 'react';
import { createApiClient, onSessionExpired } from '@ytest/api-client';
import { tokenStorage } from './storage';
const AuthContext = createContext(null);
export function AuthProvider({ children, identityApiUrl }) {
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const api = createApiClient({
        baseUrl: identityApiUrl,
        getToken: tokenStorage.getAccessToken,
        onUnauthorized: () => {
            tokenStorage.clearAll();
            setUser(null);
            setTenant(null);
        },
    });
    // Initialize auth state from storage
    useEffect(() => {
        const initAuth = async () => {
            const token = tokenStorage.getAccessToken();
            if (token) {
                try {
                    // Verify token by fetching current user
                    const response = await api.get('/api/v1/auth/me');
                    setUser(response.user);
                    setTenant(response.tenant);
                    tokenStorage.setUser(response.user);
                    tokenStorage.setTenant(response.tenant);
                }
                catch {
                    // Token invalid, clear storage
                    tokenStorage.clearAll();
                }
            }
            setIsLoading(false);
        };
        initAuth();
    }, []);
    // Listen for session expiry events
    useEffect(() => {
        return onSessionExpired(() => {
            tokenStorage.clearAll();
            setUser(null);
            setTenant(null);
        });
    }, []);
    const login = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.post('/api/v1/auth/login', data);
            tokenStorage.setTokens(response.tokens.access_token, response.tokens.refresh_token);
            tokenStorage.setUser(response.user);
            tokenStorage.setTenant(response.tenant);
            setUser(response.user);
            setTenant(response.tenant);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed');
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [api]);
    const signup = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.post('/api/v1/auth/signup', data);
            tokenStorage.setTokens(response.tokens.access_token, response.tokens.refresh_token);
            tokenStorage.setUser(response.user);
            tokenStorage.setTenant(response.tenant);
            setUser(response.user);
            setTenant(response.tenant);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Signup failed');
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [api]);
    const logout = useCallback(() => {
        tokenStorage.clearAll();
        setUser(null);
        setTenant(null);
        setError(null);
    }, []);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    const value = {
        user,
        tenant,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        signup,
        logout,
        clearError,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
//# sourceMappingURL=AuthProvider.js.map